// src/pages/CalendarPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, X, Clock, MapPin,
  Users2, Tag, Edit2, Trash2, Calendar, AlignLeft
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';

// ─── Helpers ────────────────────────────────────────────────────────────────
const DAYS_RO = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'];
const MONTHS_RO = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];

const EVENT_COLORS = [
  { label: 'Albastru',  value: '#4f8ef7' },
  { label: 'Verde',     value: '#22c55e' },
  { label: 'Roșu',      value: '#ef4444' },
  { label: 'Portocaliu',value: '#f59e0b' },
  { label: 'Violet',    value: '#8b5cf6' },
  { label: 'Cyan',      value: '#06b6d4' },
  { label: 'Roz',       value: '#ec4899' },
];

const EVENT_CATEGORIES = ['Întâlnire', 'Apel', 'Task', 'Deadline', 'Factură', 'Reînnoire', 'Altul'];

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  // Start from Monday
  let startDow = first.getDay(); // 0=Sun
  startDow = startDow === 0 ? 6 : startDow - 1; // convert to Mon=0

  const days = [];
  // Prev month padding
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, current: false });
  }
  // Current month
  for (let i = 1; i <= last.getDate(); i++) {
    days.push({ date: new Date(year, month, i), current: true });
  }
  // Next month padding to fill 6 rows
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), current: false });
  }
  return days;
}

// ─── Local storage for events (no backend needed for calendar) ──────────────
const STORAGE_KEY = 'crm_calendar_events';

function loadEvents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : getDefaultEvents();
  } catch { return getDefaultEvents(); }
}

function saveEvents(events) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(events)); } catch {}
}

function getDefaultEvents() {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth();
  return [
    { id: uuidv4(), title: 'Întâlnire client TechSoft', date: new Date(y, m, today.getDate() + 1).toISOString().slice(0,10), time: '10:00', endTime: '11:00', color: '#4f8ef7', category: 'Întâlnire', location: 'Birou central', description: 'Discutare propunere nouă infrastructură', allDay: false },
    { id: uuidv4(), title: 'Reînnoire domeniu techsoft.ro', date: new Date(y, m, today.getDate() + 5).toISOString().slice(0,10), time: '', endTime: '', color: '#ef4444', category: 'Reînnoire', location: '', description: 'Reînnoire anuală registrar RoTLD', allDay: true },
    { id: uuidv4(), title: 'Apel WebDesign Pro', date: new Date(y, m, today.getDate() + 3).toISOString().slice(0,10), time: '14:00', endTime: '14:30', color: '#22c55e', category: 'Apel', location: '', description: '', allDay: false },
    { id: uuidv4(), title: 'Deadline livrare proiect', date: new Date(y, m, today.getDate() + 7).toISOString().slice(0,10), time: '18:00', endTime: '', color: '#f59e0b', category: 'Deadline', location: '', description: '', allDay: false },
    { id: uuidv4(), title: 'Factură lunară hosting', date: new Date(y, m, 1).toISOString().slice(0,10), time: '', endTime: '', color: '#8b5cf6', category: 'Factură', location: '', description: 'Plată automată Hetzner', allDay: true },
  ];
}

// ─── Event Modal ─────────────────────────────────────────────────────────────
function EventModal({ event, defaultDate, onClose, onSave, onDelete }) {
  const isEdit = !!event?.id;
  const [f, setF] = useState(event || {
    title: '', date: defaultDate || new Date().toISOString().slice(0, 10),
    time: '', endTime: '', color: '#4f8ef7', category: 'Întâlnire',
    location: '', description: '', allDay: false
  });
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = () => {
    if (!f.title.trim()) return toast.error('Titlul este obligatoriu');
    if (!f.date) return toast.error('Data este obligatorie');
    onSave({ ...f, id: f.id || uuidv4() });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: f.color }} />
            <span className="modal-title">{isEdit ? 'Editare Eveniment' : 'Eveniment Nou'}</span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {/* Title */}
          <div className="form-group">
            <label className="form-label">Titlu *</label>
            <input className="form-input" value={f.title} onChange={e => set('title', e.target.value)} placeholder="Titlu eveniment..." autoFocus />
          </div>

          {/* Date & Time */}
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Data *</label>
              <input className="form-input" type="date" value={f.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Tot ziua</label>
              <select className="form-select" value={f.allDay ? '1' : '0'} onChange={e => set('allDay', e.target.value === '1')}>
                <option value="0">Nu (cu orar)</option>
                <option value="1">Da (toată ziua)</option>
              </select>
            </div>
          </div>

          {!f.allDay && (
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Ora Început</label>
                <input className="form-input" type="time" value={f.time || ''} onChange={e => set('time', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Ora Sfârșit</label>
                <input className="form-input" type="time" value={f.endTime || ''} onChange={e => set('endTime', e.target.value)} />
              </div>
            </div>
          )}

          {/* Category & Color */}
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Categorie</label>
              <select className="form-select" value={f.category} onChange={e => set('category', e.target.value)}>
                {EVENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Culoare</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {EVENT_COLORS.map(c => (
                  <div key={c.value} title={c.label} onClick={() => set('color', c.value)}
                    style={{ width: 22, height: 22, borderRadius: '50%', background: c.value, cursor: 'pointer', border: f.color === c.value ? '2px solid white' : '2px solid transparent', boxShadow: f.color === c.value ? `0 0 0 2px ${c.value}` : 'none', transition: 'all 0.15s' }} />
                ))}
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="form-label">Locație</label>
            <input className="form-input" value={f.location || ''} onChange={e => set('location', e.target.value)} placeholder="Adresă sau link..." />
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Descriere</label>
            <textarea className="form-textarea" value={f.description || ''} onChange={e => set('description', e.target.value)} placeholder="Note suplimentare..." style={{ minHeight: 70 }} />
          </div>
        </div>
        <div className="modal-footer">
          {isEdit && (
            <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => onDelete(f.id)}>
              <Trash2 size={14} /> Șterge
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save}>
            <Calendar size={14} /> {isEdit ? 'Salvare' : 'Adaugă'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Day Events List ─────────────────────────────────────────────────────────
function DayPanel({ date, events, onAdd, onEdit, onClose }) {
  const dayEvents = events.filter(e => e.date === date.toISOString().slice(0, 10))
    .sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 440, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>
            {date.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={onAdd}><Plus size={13} /> Adaugă</button>
            <button className="btn-icon" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {dayEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
              Niciun eveniment în această zi.
              <br /><button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={onAdd}><Plus size={12} /> Adaugă eveniment</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dayEvents.map(e => (
                <div key={e.id} onClick={() => onEdit(e)}
                  style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'var(--bg-hover)', borderRadius: 8, cursor: 'pointer', borderLeft: `3px solid ${e.color}`, transition: 'background 0.15s' }}
                  onMouseEnter={el => el.currentTarget.style.background = 'var(--border)'}
                  onMouseLeave={el => el.currentTarget.style.background = 'var(--bg-hover)'}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: e.color }}>{e.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {e.allDay ? <span>Toată ziua</span> : e.time ? <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={10} />{e.time}{e.endTime ? ` – ${e.endTime}` : ''}</span> : null}
                      {e.location && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={10} />{e.location}</span>}
                      {e.category && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Tag size={10} />{e.category}</span>}
                    </div>
                    {e.description && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{e.description}</div>}
                  </div>
                  <Edit2 size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 2 }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mini Calendar (sidebar) ─────────────────────────────────────────────────
function MiniCalendar({ current, selected, events, onSelect, onNavigate }) {
  const days = getMonthDays(current.getFullYear(), current.getMonth());
  const today = new Date();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button className="btn-icon" onClick={() => onNavigate(-1)}><ChevronLeft size={14} /></button>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{MONTHS_RO[current.getMonth()]} {current.getFullYear()}</span>
        <button className="btn-icon" onClick={() => onNavigate(1)}><ChevronRight size={14} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {DAYS_RO.map(d => <div key={d} style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center', padding: '2px 0' }}>{d[0]}</div>)}
        {days.map(({ date, current: cur }, i) => {
          const hasEv = events.some(e => e.date === date.toISOString().slice(0, 10));
          const isToday = isSameDay(date, today);
          const isSel = selected && isSameDay(date, selected);
          return (
            <div key={i} onClick={() => onSelect(date)}
              style={{ padding: '4px 2px', textAlign: 'center', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: isToday ? 700 : 400, color: !cur ? 'var(--border)' : isSel ? 'white' : isToday ? 'var(--accent)' : 'var(--text-primary)', background: isSel ? 'var(--accent)' : isToday && !isSel ? 'var(--accent)15' : 'transparent', position: 'relative' }}>
              {date.getDate()}
              {hasEv && !isSel && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--accent)', margin: '1px auto 0' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Calendar Page ──────────────────────────────────────────────────────
export default function CalendarPage() {
  const [events, setEvents] = useState(() => loadEvents());
  const [current, setCurrent] = useState(new Date());
  const [view, setView] = useState('month'); // month | week | list
  const [selectedDay, setSelectedDay] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [defaultDate, setDefaultDate] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const toast = useToast();

  const today = new Date();
  const days = getMonthDays(current.getFullYear(), current.getMonth());

  const filteredEvents = filterCat ? events.filter(e => e.category === filterCat) : events;

  const navigate = (dir) => {
    const d = new Date(current);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    setCurrent(d);
  };

  const openAdd = (date = null) => {
    setDefaultDate(date ? date.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
    setEditEvent(null);
    setShowModal(true);
    setSelectedDay(null);
  };

  const openEdit = (ev) => {
    setEditEvent(ev);
    setShowModal(true);
    setSelectedDay(null);
  };

  const handleSave = (ev) => {
    const updated = editEvent
      ? events.map(e => e.id === ev.id ? ev : e)
      : [...events, ev];
    setEvents(updated);
    saveEvents(updated);
    setShowModal(false);
    toast.success(editEvent ? 'Eveniment actualizat' : 'Eveniment adăugat');
  };

  const handleDelete = (id) => {
    if (!window.confirm('Ștergi evenimentul?')) return;
    const updated = events.filter(e => e.id !== id);
    setEvents(updated);
    saveEvents(updated);
    setShowModal(false);
    toast.success('Eveniment șters');
  };

  // Get week days
  const getWeekDays = () => {
    const start = new Date(current);
    const dow = start.getDay() === 0 ? 6 : start.getDay() - 1;
    start.setDate(start.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // Upcoming events (list view)
  const upcomingEvents = [...filteredEvents]
    .filter(e => new Date(e.date) >= new Date(today.toISOString().slice(0, 10)))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''));

  const todayEvents = filteredEvents.filter(e => e.date === today.toISOString().slice(0, 10));

  return (
    <div className="fade-in" style={{ display: 'flex', gap: 20, height: 'calc(100vh - 108px)', overflow: 'hidden' }}>

      {/* ── Sidebar ── */}
      <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
        {/* Add button */}
        <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={() => openAdd()}>
          <Plus size={15} /> Eveniment Nou
        </button>

        {/* Mini calendar */}
        <div className="card" style={{ padding: 14 }}>
          <MiniCalendar
            current={current}
            selected={selectedDay}
            events={filteredEvents}
            onSelect={d => { setSelectedDay(d); setCurrent(d); }}
            onNavigate={navigate}
          />
        </div>

        {/* Filter by category */}
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Categorii</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button onClick={() => setFilterCat('')}
              style={{ textAlign: 'left', padding: '5px 8px', borderRadius: 5, border: 'none', background: !filterCat ? 'var(--accent)20' : 'transparent', color: !filterCat ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: !filterCat ? 600 : 400 }}>
              Toate ({events.length})
            </button>
            {EVENT_CATEGORIES.map(cat => {
              const cnt = events.filter(e => e.category === cat).length;
              if (cnt === 0) return null;
              return (
                <button key={cat} onClick={() => setFilterCat(cat === filterCat ? '' : cat)}
                  style={{ textAlign: 'left', padding: '5px 8px', borderRadius: 5, border: 'none', background: filterCat === cat ? 'var(--accent)20' : 'transparent', color: filterCat === cat ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: filterCat === cat ? 600 : 400 }}>
                  {cat} ({cnt})
                </button>
              );
            })}
          </div>
        </div>

        {/* Today's events */}
        {todayEvents.length > 0 && (
          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Azi</div>
            {todayEvents.map(e => (
              <div key={e.id} onClick={() => openEdit(e)}
                style={{ padding: '6px 0', borderBottom: '1px solid var(--border)33', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: e.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</span>
                </div>
                {e.time && <div style={{ fontSize: 10, color: 'var(--text-secondary)', paddingLeft: 12 }}>{e.time}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Main Area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexShrink: 0 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrent(new Date())}>Azi</button>
          <div style={{ display: 'flex', gap: 2 }}>
            <button className="btn-icon" onClick={() => navigate(-1)}><ChevronLeft size={16} /></button>
            <button className="btn-icon" onClick={() => navigate(1)}><ChevronRight size={16} /></button>
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
            {view === 'week'
              ? (() => { const wd = getWeekDays(); return `${wd[0].getDate()} – ${wd[6].getDate()} ${MONTHS_RO[wd[0].getMonth()]} ${wd[0].getFullYear()}`; })()
              : view === 'list'
              ? 'Evenimente Viitoare'
              : `${MONTHS_RO[current.getMonth()]} ${current.getFullYear()}`}
          </h2>
          <div style={{ display: 'flex', gap: 3, background: 'var(--bg-hover)', borderRadius: 7, padding: 3 }}>
            {['month', 'week', 'list'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '4px 12px', borderRadius: 5, border: 'none', background: view === v ? 'var(--bg-card)' : 'transparent', color: view === v ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: view === v ? 600 : 400, transition: 'all 0.15s' }}>
                {v === 'month' ? 'Lună' : v === 'week' ? 'Săptămână' : 'Listă'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Month View ── */}
        {view === 'month' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4, flexShrink: 0 }}>
              {DAYS_RO.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', padding: '4px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{d}</div>
              ))}
            </div>
            {/* Grid */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)', gap: 2, overflow: 'hidden' }}>
              {days.map(({ date, current: cur }, i) => {
                const dateStr = date.toISOString().slice(0, 10);
                const dayEvs = filteredEvents.filter(e => e.date === dateStr)
                  .sort((a, b) => (a.time || '99').localeCompare(b.time || '99'));
                const isToday2 = isSameDay(date, today);
                const isSel = selectedDay && isSameDay(date, selectedDay);
                return (
                  <div key={i} onClick={() => setSelectedDay(date)}
                    style={{ border: `1px solid ${isSel ? 'var(--accent)' : 'var(--border)33'}`, borderRadius: 6, padding: '5px 6px', cursor: 'pointer', background: isToday2 ? 'var(--accent)08' : isSel ? 'var(--accent)10' : 'var(--bg-card)', overflow: 'hidden', transition: 'border-color 0.15s', minHeight: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: isToday2 ? 800 : 500, color: !cur ? 'var(--border)' : isToday2 ? 'white' : 'var(--text-primary)', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: isToday2 ? 'var(--accent)' : 'transparent' }}>
                        {date.getDate()}
                      </span>
                      {dayEvs.length > 0 && <span style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{dayEvs.length}</span>}
                    </div>
                    {dayEvs.slice(0, 3).map(e => (
                      <div key={e.id} onClick={ev => { ev.stopPropagation(); openEdit(e); }}
                        style={{ fontSize: 10, padding: '1px 4px', borderRadius: 3, background: `${e.color}25`, color: e.color, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {!e.allDay && e.time && `${e.time} `}{e.title}
                      </div>
                    ))}
                    {dayEvs.length > 3 && <div style={{ fontSize: 9, color: 'var(--text-secondary)', padding: '1px 4px' }}>+{dayEvs.length - 3} mai mult</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Week View ── */}
        {view === 'week' && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, flex: 1, overflowY: 'auto' }}>
              {getWeekDays().map((date, i) => {
                const dateStr = date.toISOString().slice(0, 10);
                const dayEvs = filteredEvents.filter(e => e.date === dateStr)
                  .sort((a, b) => (a.time || '99').localeCompare(b.time || '99'));
                const isToday2 = isSameDay(date, today);
                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 8, background: isToday2 ? 'var(--accent)' : 'var(--bg-hover)', cursor: 'pointer' }} onClick={() => openAdd(date)}>
                      <div style={{ fontSize: 10, color: isToday2 ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 700 }}>{DAYS_RO[i]}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: isToday2 ? 'white' : 'var(--text-primary)', lineHeight: 1.1 }}>{date.getDate()}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {dayEvs.map(e => (
                        <div key={e.id} onClick={() => openEdit(e)}
                          style={{ padding: '6px 8px', borderRadius: 6, background: `${e.color}20`, borderLeft: `3px solid ${e.color}`, cursor: 'pointer', fontSize: 11 }}>
                          <div style={{ fontWeight: 600, color: e.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</div>
                          {e.time && <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2 }}>{e.time}</div>}
                        </div>
                      ))}
                      {dayEvs.length === 0 && (
                        <div onClick={() => openAdd(date)} style={{ padding: '10px 4px', textAlign: 'center', color: 'var(--border)', fontSize: 20, cursor: 'pointer', borderRadius: 6, border: '1px dashed var(--border)' }}>+</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── List View ── */}
        {view === 'list' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {upcomingEvents.length === 0 ? (
              <div className="empty-state">
                <Calendar size={40} opacity={0.2} style={{ margin: '0 auto 16px', display: 'block' }} />
                <div className="empty-state-title">Niciun eveniment viitor</div>
                <button className="btn btn-primary btn-sm" style={{ margin: '12px auto 0', display: 'flex' }} onClick={() => openAdd()}>
                  <Plus size={13} /> Adaugă eveniment
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {upcomingEvents.reduce((acc, e, i, arr) => {
                  const prev = arr[i - 1];
                  if (!prev || prev.date !== e.date) {
                    const d = new Date(e.date);
                    const isToday3 = e.date === today.toISOString().slice(0, 10);
                    acc.push(
                      <div key={`h-${e.date}`} style={{ padding: '12px 0 4px', fontSize: 12, fontWeight: 700, color: isToday3 ? 'var(--accent)' : 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {isToday3 ? '● Azi' : d.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </div>
                    );
                  }
                  acc.push(
                    <div key={e.id} onClick={() => openEdit(e)}
                      style={{ display: 'flex', gap: 14, padding: '12px 16px', borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border)33', cursor: 'pointer', borderLeft: `3px solid ${e.color}` }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{e.title}</div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                          {e.allDay
                            ? <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={10} />Toată ziua</span>
                            : e.time && <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={10} />{e.time}{e.endTime ? ` – ${e.endTime}` : ''}</span>}
                          {e.location && <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={10} />{e.location}</span>}
                          <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 10, background: `${e.color}20`, color: e.color }}>{e.category}</span>
                        </div>
                        {e.description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{e.description}</div>}
                      </div>
                      <Edit2 size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 2 }} />
                    </div>
                  );
                  return acc;
                }, [])}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Day Panel ── */}
      {selectedDay && view === 'month' && (
        <DayPanel
          date={selectedDay}
          events={filteredEvents}
          onAdd={() => openAdd(selectedDay)}
          onEdit={openEdit}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {/* ── Event Modal ── */}
      {showModal && (
        <EventModal
          event={editEvent}
          defaultDate={defaultDate}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
